import React, { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import { convertToFloat } from 'utils/numbers';
import { isNotEmptyArray, isNullOrUndefined } from 'utils/misc';
import cancellationTypes from 'constants/cancellationTypes';
import permissionTypes from 'constants/permissionTypes';
import FormLayout from '../common/FormLayout';
import commonPropTypes from '../common/commonPropTypes';
import { getCancellationFeeValidators } from '../common/validators';
import NoticePeriod from '../common/NoticePeriod';
import CancelFeeDeclaration from '../common/CancelFeeDeclaration';
import NoPermissionError from '../common/NoPermissionError';

import * as Styled from '../CancellationForm.styled';

const NoOverdueForm = (props) => {
  const {
    cancellationReasons,
    onFormSubmit,
    isUpdatingAccount,
    adfitNo,
    estimatedFinalBillDate,
    localisation,
    recentNotClearedPayment,
    account,
    balanceToSettleWithoutCancelFee,
    facility,
    cancelDaysNotice,
    permissions,
  } = props;

  const {
    outsideTerm,
    cancelFee,
    country,
    isFixedTerm,
    isTermIdentified,
    hasPy,
    isBiPartite,
  } = account;

  const { industryType } = facility;

  const { dateFormat } = localisation.locales;

  const cancellationOptions = useMemo(() => {
    const endOfTermOption =
      !outsideTerm && isTermIdentified
        ? [
            {
              label: 'Cancel at the end of the term',
              subLabels: [
                ...(estimatedFinalBillDate && hasPy
                  ? [
                      `Estimated final payment date ${moment(
                        estimatedFinalBillDate
                      ).format(dateFormat)}`,
                    ]
                  : []),
              ],
              value: cancellationTypes.SET_TO_TERM,
            },
          ]
        : [];

    const noticerPeriodOption =
      //An account can be set to term and can still be active outside term when there are suspensions in between or an overdue amount is pending to be collected.
      // In that case notice period doesn't apply.
      outsideTerm && !isFixedTerm && isTermIdentified
        ? [
            {
              label: `Cancel after ${cancelDaysNotice} days notice period`,
              value: cancellationTypes.WITH_NOTICE_PERIOD,
              subLabels: [],
            },
          ]
        : [];

    //If only has CanSetToTerm permission then display below options.
    if (
      permissions.includes(permissionTypes.canSetToTerm) &&
      !permissions.includes(permissionTypes.canCancelAllAccounts)
    ) {
      return [...endOfTermOption, ...noticerPeriodOption];
    }

    //Display all the options.
    return [
      {
        label: 'Cancel immediately',
        subLabels: [
          ...(recentNotClearedPayment
            ? ['Payment will not be re-attempted upon reversal']
            : []),
        ],
        value: cancellationTypes.CANCEL_STRAIGHT_DOWN,
      },
      // Estimated final bill date is today then this option should be hidden.
      ...(!outsideTerm &&
      hasPy &&
      balanceToSettleWithoutCancelFee > 0 &&
      !moment(estimatedFinalBillDate).isSame(moment(), 'days')
        ? [
            {
              label: 'Collect cancellation fee and cancel',
              subLabels: [],
              value: cancellationTypes.COLLECT_CANCELLATION_FEE,
            },
          ]
        : []),
      ...endOfTermOption,
      ...noticerPeriodOption,
    ];
  }, [
    dateFormat,
    estimatedFinalBillDate,
    recentNotClearedPayment,
    outsideTerm,
    cancelDaysNotice,
    balanceToSettleWithoutCancelFee,
    isFixedTerm,
    permissions,
  ]);

  const { handleSubmit, control, watch, errors, getValues } = useForm({
    defaultValues: {
      cancellationOption: isNotEmptyArray(cancellationOptions)
        ? cancellationOptions[0].value
        : undefined,
      cancelReason: null,
      cancellationFee: null,
      noticeStartDate: moment(),
      cancellationFeeDeclaration: false,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const watchCancellationOption = watch('cancellationOption');
  //Won't rerender unless watched.
  const watchCancellationFee = watch('cancellationFee');

  const onValidSubmit = useCallback(
    (data) => {
      onFormSubmit(cancellationOptions, data);
    },
    [cancellationOptions, onFormSubmit]
  );

  if (!isNotEmptyArray(cancellationOptions)) {
    return <NoPermissionError />;
  }

  return (
    <FormLayout
      onSubmit={handleSubmit(onValidSubmit)}
      isSubmittingForm={isUpdatingAccount}
      formControl={control}
      adfitNo={adfitNo}
      dataTestId={'no-overdue-form'}
      cancellationOptions={cancellationOptions}
      cancellationReasons={cancellationReasons}>
      <div />
      {isTermIdentified &&
        watchCancellationOption ===
          cancellationTypes.COLLECT_CANCELLATION_FEE && (
          <FormLayout.InputWrapper>
            <Controller
              name={'cancellationFee'}
              control={control}
              rules={{
                required: 'Please enter a value',
                min: {
                  value: 1.0,
                  message: 'Cancellation fee must be $1.00 or greater',
                },
                validate: {
                  ...getCancellationFeeValidators(getValues, {
                    cancelFee,
                    balanceToSettleWithoutCancelFee,
                    industryType,
                    country,
                    isBiPartite,
                  }),
                },
              }}
              render={({ onChange, name, value, onBlur }) => (
                <Styled.FormInput
                  name={name}
                  type={'number'}
                  step={0.01}
                  min={1.0}
                  disabled={isUpdatingAccount}
                  value={isNullOrUndefined(value) ? '' : value}
                  onChange={(event) => {
                    if (event.target.validity.valid) {
                      onChange(convertToFloat(event.target.value));
                    }
                  }}
                  startAdornment={'$'}
                  showStartAdornmentDivider={true}
                  showInlineIcons={false}
                  labelId={'cancellationFeeId'}
                  label={'Cancellation fee'}
                  onBlur={onBlur}
                  error={
                    errors.cancellationFee && errors.cancellationFee.message
                  }
                />
              )}
            />
            <CancelFeeDeclaration
              cancelFeeLimit={cancelFee}
              formControl={control}
              cancellationFeeValue={watchCancellationFee}
              cancellationFeeError={errors.cancellationFee}
              declarationError={errors.cancellationFeeDeclaration}
            />
          </FormLayout.InputWrapper>
        )}
      {watchCancellationOption === cancellationTypes.WITH_NOTICE_PERIOD && (
        <FormLayout.InputWrapper>
          <NoticePeriod
            formControl={control}
            cancelDaysNotice={cancelDaysNotice}
            isSubmittingForm={isUpdatingAccount}
            dateDisplayFormat={dateFormat}
            noticeErrors={errors.noticeStartDate}
          />
        </FormLayout.InputWrapper>
      )}
    </FormLayout>
  );
};

NoOverdueForm.propTypes = {
  ...commonPropTypes,
};

NoOverdueForm.defaultProps = {
  estimatedFinalBillDate: null,
  balanceToSettleWithoutCancelFee: null,
};

export default NoOverdueForm;
